import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
  useColorModeValue,
  VStack,
  IconButton,
  Textarea,
  useToast,
  Icon,
  Badge,
  HStack,
  Tooltip,
} from "@chakra-ui/react";
import { FaComment, FaCheck, FaCheckDouble, FaTrash } from "react-icons/fa6";
import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import websocketService from "services/websocket";
import usePersistedState from "utilities/usePersistedState";

const TypingIndicator = () => {
  return (
    <Flex align="center" gap={1}>
      <Box
        animation="typing 1s infinite"
        bg="gray.500"
        borderRadius="full"
        h="4px"
        style={{ animationDelay: "0ms" }}
        w="4px"
      />
      <Box
        animation="typing 1s infinite"
        bg="gray.500"
        borderRadius="full"
        h="4px"
        style={{ animationDelay: "200ms" }}
        w="4px"
      />
      <Box
        animation="typing 1s infinite"
        bg="gray.500"
        borderRadius="full"
        h="4px"
        style={{ animationDelay: "400ms" }}
        w="4px"
      />
      <style>
        {`
          @keyframes typing {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-4px); }
          }
        `}
      </style>
    </Flex>
  );
};

const MessageBubble = ({ message, isUser, isTyping, timestamp, status }) => {
  const bgColor = isUser ? "blue.500" : "gray.200";
  const textColor = isUser ? "white" : "black";
  const alignSelf = isUser ? "flex-end" : "flex-start";
  const borderRadius = isUser ? "20px 20px 0 20px" : "20px 20px 20px 0";
  const timestampColor = isUser ? "whiteAlpha.700" : "gray.500";

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box
      alignSelf={alignSelf}
      bg={bgColor}
      borderRadius={borderRadius}
      color={textColor}
      maxW="70%"
      mb={2}
      p={3}
      position="relative"
    >
      {message ? (
        <>
          <Text whiteSpace="pre-wrap">{message}</Text>
          {!!isTyping && !isUser && (
            <Box
              animation="streaming 1s linear infinite"
              bg="linear-gradient(90deg, transparent, blue.500, transparent)"
              bottom="-2px"
              height="2px"
              left="0"
              position="absolute"
              right="0"
            >
              <style>
                {`
                  @keyframes streaming {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                  }
                `}
              </style>
            </Box>
          )}
        </>
      ) : (
        isTyping && !isUser && <TypingIndicator />
      )}
      <HStack justify="space-between" mt={1} spacing={2} w="100%">
        <Text color={timestampColor} fontSize="xs">
          {formatTime(timestamp)}
        </Text>
        {!!isUser && !!status && (
          <Tooltip label={status === "sent" ? "Sent" : "Delivered"}>
            <Box>
              <Icon
                as={status === "sent" ? FaCheck : FaCheckDouble}
                color={timestampColor}
                h={3}
                w={3}
              />
            </Box>
          </Tooltip>
        )}
      </HStack>
    </Box>
  );
};

export const ChatDrawer = ({ isOpen, onClose }) => {
  const { projectId } = useParams();
  const [messages, setMessages] = usePersistedState(
    `chat_history_${projectId}`,
    []
  );
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const bgColor = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const toast = useToast();

  // Initialize socket connection
  useEffect(() => {
    if (isOpen && projectId) {
      // Connect to websocket service
      websocketService.connect();
      setIsConnected(true);

      // Join project room
      websocketService.joinProject(projectId);

      // Set up event listeners
      const handleChatEvent = (data) => {
        console.log("Chat event received:", data);

        if (
          data.event.type === "content_block_delta" &&
          data.event.delta.type === "text_delta"
        ) {
          console.log("Processing text delta:", data.event.delta.text);
          setMessages((prev) => {
            // Find the last assistant message
            const lastAssistantMessage = prev.findLast((msg) => !msg.isUser);
            console.log("Last assistant message:", lastAssistantMessage);

            if (lastAssistantMessage) {
              // Update the last assistant message
              return prev.map((msg) =>
                msg === lastAssistantMessage
                  ? {
                      ...msg,
                      text: msg.text + data.event.delta.text,
                      isTyping: true,
                    }
                  : msg
              );
            } else {
              // Create new message
              return [
                ...prev,
                {
                  text: data.event.delta.text,
                  isUser: false,
                  isTyping: true,
                  timestamp: Date.now(),
                },
              ];
            }
          });
        }
        // Handle message start
        else if (data.event.type === "message_start") {
          console.log("Message start event");
        }
        // Handle message stop
        else if (data.event.type === "message_stop") {
          console.log("Message stop event");
          setMessages((prev) => {
            // Find the last assistant message
            const lastAssistantMessage = prev.findLast((msg) => !msg.isUser);
            if (lastAssistantMessage) {
              // Update the last assistant message to remove typing state
              return prev.map((msg) =>
                msg === lastAssistantMessage ? { ...msg, isTyping: false } : msg
              );
            }
            return prev;
          });
          setIsSending(false);
        }
      };

      const handleChatCleared = (data) => {
        console.log("Chat cleared:", data);
        setMessages([]);
      };

      // Add event listeners
      websocketService.addEventListener("chat_event", handleChatEvent);
      websocketService.addEventListener("chat_cleared", handleChatCleared);

      // Cleanup on unmount
      return () => {
        websocketService.removeEventListener("chat_event", handleChatEvent);
        websocketService.removeEventListener("chat_cleared", handleChatCleared);
        websocketService.leaveProject(projectId);
      };
    }
  }, [isOpen, projectId, toast, setMessages]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const maxHeight = 5 * 24;
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isSending && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isSending]);

  const handleSendMessage = () => {
    if (!inputValue.trim() || isSending || !isConnected) return;

    const userMessage = inputValue;
    const timestamp = Date.now();
    setInputValue("");
    setIsSending(true);

    // Add user message immediately
    setMessages((prev) => [
      ...prev,
      {
        text: userMessage,
        isUser: true,
        timestamp,
        status: "sent",
      },
    ]);

    // Add empty assistant message with typing indicator
    setMessages((prev) => [
      ...prev,
      {
        text: "",
        isUser: false,
        isTyping: true,
        timestamp: Date.now(),
      },
    ]);

    // Send message through websocket service
    websocketService.socket.emit("chat_message", {
      project_id: projectId,
      message: userMessage,
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleClearChat = () => {
    websocketService.socket.emit("clear_chat", { project_id: projectId });
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent bg={bgColor}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
          <HStack justify="space-between" w="100%">
            <HStack spacing={4}>
              <Text>Chat Assistant</Text>
              <Flex align="center" gap={2}>
                <Badge
                  colorScheme={isConnected ? "green" : "red"}
                  fontSize="xs"
                  variant="subtle"
                >
                  {isConnected ? "Connected" : "Disconnected"}
                </Badge>
              </Flex>
              <Tooltip label="Clear chat history">
                <IconButton
                  aria-label="Clear chat"
                  colorScheme="gray"
                  icon={<Icon as={FaTrash} />}
                  onClick={handleClearChat}
                  size="sm"
                  variant="ghost"
                />
              </Tooltip>
            </HStack>
          </HStack>
        </DrawerHeader>
        <DrawerBody display="flex" flexDirection="column" p={0}>
          <VStack align="stretch" flex={1} overflowY="auto" p={4} spacing={4}>
            {messages.map((message, index) => (
              <MessageBubble
                isDebug={message.isDebug}
                isSystem={message.isSystem}
                isTyping={message.isTyping}
                isUser={message.isUser}
                key={index}
                message={message.text}
                status={message.status}
                timestamp={message.timestamp}
              />
            ))}
            <div ref={messagesEndRef} />
          </VStack>
          <Flex
            align="center"
            borderColor={borderColor}
            borderTopWidth="1px"
            p={4}
          >
            <Textarea
              isDisabled={isSending || !isConnected}
              minH="24px"
              mr={2}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              overflowY="auto"
              p={2}
              placeholder={
                !isConnected
                  ? "Connecting..."
                  : "Type a message... (Shift+Enter for new line)"
              }
              ref={textareaRef}
              resize="none"
              style={{
                transition: "height 0.2s ease",
              }}
              value={inputValue}
            />
            <IconButton
              aria-label="Send message"
              colorScheme="blue"
              icon={<Icon as={FaComment} />}
              isDisabled={!inputValue.trim() || isSending || !isConnected}
              isLoading={isSending}
              onClick={handleSendMessage}
            />
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
